import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { vendutoApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Upload, FileSpreadsheet, Loader2, AlertTriangle, CheckCircle2, Trash2, Database, ShieldCheck,
} from 'lucide-react';
import VendutoHeader from '../components/venduto/VendutoHeader';
import LinkGuida from '../components/promemoria/LinkGuida';
import { fmtEuro, fmtNum } from '../lib/venduto';
import { cn } from '../lib/utils';

const LIVELLO_STYLE = {
  alto: 'border-red-300 bg-red-50 text-red-900',
  medio: 'border-amber-300 bg-amber-50 text-amber-900',
  basso: 'border-blue-200 bg-blue-50 text-blue-900',
};

const VendutoCaricamentoPage = () => {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [stato, setStato] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [consolidando, setConsolidando] = useState(false);
  const [risultato, setRisultato] = useState(null);
  const [confermaOpen, setConfermaOpen] = useState(false);

  const caricaStato = useCallback(async () => {
    try {
      const s = await vendutoApi.getStato();
      setStato(s);
      if (s.upload_pendente && !preview) {
        try {
          setPreview(await vendutoApi.getUpload(s.upload_pendente.upload_id));
        } catch (e) { /* caricamento non più valido */ }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel caricamento dello stato');
    }
  }, [preview]);

  useEffect(() => { caricaStato(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const invalidi = files.filter((f) => !f.name.toLowerCase().match(/\.xlsx?$/));
    if (invalidi.length) {
      toast.error('Carica solo file Excel (.xlsx) esportati da Cassa in Cloud');
      return;
    }
    setLoading(true);
    setRisultato(null);
    try {
      const p = await vendutoApi.uploadPreview(files);
      setPreview(p);
      if (p.errori_file?.length) p.errori_file.forEach((err) => toast.warning(err));
      toast.success(`${p.files.length} file letti: ${fmtNum(p.totali.n_documenti)} documenti`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella lettura dei file');
    } finally {
      setLoading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const cambiaTipo = async (fileId, tipo) => {
    try {
      setPreview(await vendutoApi.setTipoFile(preview.upload_id, fileId, tipo));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel cambio tipo');
    }
  };

  const consolida = async () => {
    setConsolidando(true);
    try {
      const res = await vendutoApi.consolida(preview.upload_id);
      setRisultato(res);
      setPreview(null);
      setConfermaOpen(false);
      toast.success(`Consolidato: ${res.giorni_nuovi} giorni nuovi, ${res.giorni_sostituiti} sostituiti`);
      caricaStato();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel consolidamento');
    } finally {
      setConsolidando(false);
    }
  };

  const scarta = async () => {
    try {
      await vendutoApi.scartaUpload(preview.upload_id);
      setPreview(null);
      toast.success('Caricamento scartato: nulla è stato scritto in archivio');
      caricaStato();
    } catch (e) {
      toast.error('Errore nello scarto del caricamento');
    }
  };

  return (
    <div className="min-h-screen pb-24">
      <VendutoHeader titolo="Carica venduto" sottotitolo="Passo 1: verifica · Passo 2: consolida" />

      <main className="px-4 py-5 max-w-lg mx-auto space-y-4">
        <LinkGuida testo="Dove si scaricano i file da Cassa in Cloud?" />
        <Card className="bg-accent border-accent-foreground/20">
          <CardContent className="p-4 text-sm space-y-2">
            <p className="font-semibold text-accent-foreground">Come funziona</p>
            <ul className="text-xs space-y-1 list-disc list-inside text-accent-foreground/90">
              <li>Esporta da Cassa in Cloud <strong>scontrini, proforma e fatture</strong> dello stesso periodo (max 6 mesi per file).</li>
              <li>Carichi i file insieme: il tipo viene riconosciuto e resta correggibile a mano.</li>
              <li>Prima di scrivere vedi l'anteprima. Il consolidamento sostituisce <strong>solo i documenti dello stesso tipo</strong> nelle giornate caricate: gli altri tipi già in archivio restano intatti.</li>
            </ul>
          </CardContent>
        </Card>

        <Card className="border-dashed border-2">
          <CardContent className="p-6">
            <input
              ref={fileRef}
              type="file"
              multiple
              accept=".xlsx,.xls"
              onChange={handleFiles}
              className="hidden"
              data-testid="venduto-file-input"
            />
            <button
              className="w-full flex flex-col items-center justify-center gap-2"
              onClick={() => fileRef.current?.click()}
              data-testid="venduto-upload-btn"
            >
              <Upload className="w-10 h-10 text-muted-foreground" />
              <span className="font-semibold">Carica e verifica</span>
              <span className="text-xs text-muted-foreground">Più file .xlsx insieme (max 12)</span>
            </button>
          </CardContent>
        </Card>

        {loading && (
          <Card><CardContent className="p-6 flex flex-col items-center gap-3">
            <Loader2 className="w-7 h-7 animate-spin text-primary" />
            <p className="text-sm">Lettura dei file in corso…</p>
          </CardContent></Card>
        )}

        {preview && (
          <>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Anteprima del caricamento</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3 text-center">
                  <div className="p-3 rounded-lg bg-secondary">
                    <p className="text-xl font-bold font-mono-numbers" data-testid="preview-righe">{fmtNum(preview.totali.n_righe)}</p>
                    <p className="text-xs text-muted-foreground">Righe lette</p>
                  </div>
                  <div className="p-3 rounded-lg bg-secondary">
                    <p className="text-xl font-bold font-mono-numbers" data-testid="preview-documenti">{fmtNum(preview.totali.n_documenti)}</p>
                    <p className="text-xs text-muted-foreground">Documenti</p>
                  </div>
                  <div className="p-3 rounded-lg bg-secondary">
                    <p className="text-xl font-bold font-mono-numbers">{fmtNum(preview.totali.n_coperti)}</p>
                    <p className="text-xs text-muted-foreground">Coperti</p>
                  </div>
                  <div className="p-3 rounded-lg bg-secondary">
                    <p className="text-xl font-bold font-mono-numbers">{fmtEuro(preview.totali.incasso)}</p>
                    <p className="text-xs text-muted-foreground">Incasso</p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground font-mono-numbers" data-testid="preview-intervallo">
                  {preview.totali.n_giorni} giorni · {preview.totali.data_min} → {preview.totali.data_max}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">File e tipo assegnato</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {preview.files.map((f) => (
                  <div key={f.file_id} className="p-3 rounded-lg border space-y-2" data-testid={`preview-file-${f.file_id}`}>
                    <div className="flex items-start gap-2">
                      <FileSpreadsheet className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold truncate">{f.filename}</p>
                        <p className="text-xs text-muted-foreground font-mono-numbers">
                          {fmtNum(f.n_documenti)} doc · {fmtNum(f.n_righe)} righe · {f.data_min} → {f.data_max}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select value={f.tipo} onValueChange={(v) => cambiaTipo(f.file_id, v)}>
                        <SelectTrigger className="h-9 w-[150px]" data-testid={`tipo-select-${f.file_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(stato?.tipi_file || []).map((t) => (
                            <SelectItem key={t.valore} value={t.valore}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {f.tipo === f.tipo_rilevato ? (
                        <Badge variant="secondary" className="text-[10px]">rilevato automaticamente</Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-900 border-amber-300 text-[10px]">corretto a mano</Badge>
                      )}
                    </div>
                    {f.warnings?.map((w, i) => (
                      <p key={i} className="text-[11px] text-muted-foreground">• {w}</p>
                    ))}
                  </div>
                ))}
              </CardContent>
            </Card>

            {preview.avvisi?.length > 0 && (
              <div className="space-y-2" data-testid="preview-avvisi">
                {preview.avvisi.map((a, i) => (
                  <div key={i} className={cn('p-3 rounded-lg border text-xs flex gap-2', LIVELLO_STYLE[a.livello])}>
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <div>
                      <p>{a.testo}</p>
                      {a.giorni?.length > 0 && (
                        <p className="mt-1 font-mono-numbers opacity-70">
                          {a.giorni.slice(0, 8).join(', ')}{a.giorni.length > 8 ? ' …' : ''}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 h-12" onClick={scarta} data-testid="scarta-btn">
                <Trash2 className="w-4 h-4 mr-2" /> Scarta
              </Button>
              <Button className="flex-[2] h-12 font-bold" onClick={() => setConfermaOpen(true)} data-testid="consolida-btn">
                <Database className="w-4 h-4 mr-2" /> Consolida in archivio
              </Button>
            </div>
          </>
        )}

        {risultato && (
          <Card className="border-green-300 bg-green-50">
            <CardContent className="p-5 text-center space-y-3">
              <CheckCircle2 className="w-12 h-12 mx-auto text-green-600" />
              <p className="font-bold text-green-900">Consolidamento completato</p>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-xl font-bold font-mono-numbers text-green-800" data-testid="res-giorni-nuovi">{risultato.giorni_nuovi}</p>
                  <p className="text-xs text-green-700">Giorni nuovi</p>
                </div>
                <div>
                  <p className="text-xl font-bold font-mono-numbers text-blue-800" data-testid="res-giorni-sostituiti">{risultato.giorni_sostituiti}</p>
                  <p className="text-xs text-blue-700">Aggiornati</p>
                </div>
                <div>
                  <p className="text-xl font-bold font-mono-numbers text-stone-700">{fmtNum(risultato.n_documenti)}</p>
                  <p className="text-xs text-stone-600">Documenti</p>
                </div>
              </div>
              {risultato.giorni_integrati > 0 && (
                <p className="text-xs text-green-800" data-testid="res-giorni-integrati">
                  {risultato.giorni_integrati} giornate già presenti sono state <strong>arricchite</strong> con
                  questi documenti, senza toccare gli altri tipi.
                </p>
              )}
              <div className="flex gap-2 justify-center">
                <Button variant="outline" onClick={() => setRisultato(null)}>Nuovo caricamento</Button>
                <Button onClick={() => navigate('/venduto')}>Vedi le metriche</Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border-dashed">
          <CardContent className="p-4 text-xs text-muted-foreground flex gap-2">
            <ShieldCheck className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <p>
              Il venduto vive in tabelle dedicate: caricarlo non modifica in alcun modo le chiusure di
              cassa, le uscite o il bilancio. Puoi comunque esportare un backup JSON dell'archivio
              dalla scheda <strong>Setup</strong>.
            </p>
          </CardContent>
        </Card>
      </main>

      <Dialog open={confermaOpen} onOpenChange={setConfermaOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Consolidare in archivio?</DialogTitle>
            <DialogDescription>
              I documenti dei tipi caricati sostituiranno quelli già presenti nelle stesse giornate.
              Gli altri tipi di documento di quelle giornate restano intatti.
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="text-sm space-y-1 p-3 rounded-md bg-secondary">
              <p><strong>Periodo:</strong> <span className="font-mono-numbers">{preview.totali.data_min} → {preview.totali.data_max}</span></p>
              <p><strong>Giorni nel caricamento:</strong> {preview.totali.n_giorni}</p>
              <p><strong>Giorni con gli stessi tipi già in archivio:</strong> {preview.giorni_da_sostituire}</p>
              {preview.tipi_gia_in_archivio?.length > 0 && (
                <p><strong>Tipi conservati in quelle giornate:</strong> {preview.tipi_gia_in_archivio.join(', ')}</p>
              )}
              <p><strong>File:</strong> {preview.files.map((f) => `${f.filename} (${f.tipo})`).join(', ')}</p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfermaOpen(false)}>Annulla</Button>
            <Button onClick={consolida} disabled={consolidando} data-testid="conferma-consolida-btn">
              {consolidando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
              Consolida
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendutoCaricamentoPage;
